import { Middleware } from '@reduxjs/toolkit';
import { Feature, logError, logUsage, setLogUser } from '../services/eventLog';
import i18n from '../i18n';

/**
 * 기능 사용 기록 미들웨어 — "어떤 사용자가 어떤 기능을 언제 썼는가" 를 한 곳에서 남긴다.
 *
 * 왜 미들웨어인가: 노드 추가 하나만 해도 캔버스에서 부르는 자리가 일곱 군데고(드롭·복제·붙여넣기·마법사·
 * AI 도구…), 저장·배포는 화면과 AI 도구가 각각 부른다. 호출 자리마다 한 줄씩 넣으면 **새로 생긴 경로에서
 * 반드시 빠진다.** 액션은 어느 경로로 왔든 여기를 지나므로, 여기 한 곳이면 빠짐이 없다.
 *
 * 비동기 액션(createAsyncThunk)은 성공(fulfilled)과 실패(rejected)가 짝으로 오므로 사용 기록과 오류 기록이
 * 같은 자리에서 나온다 — 저장이 실패한 사실이 기록에서 빠지지 않는다.
 *
 * 걸러야 하는 잡음 하나: **워크플로를 여는 것은 편집이 아니다.** 캔버스를 열면 addNode 가 수십 번 쏟아지는데
 * 그것까지 "노드를 추가했다" 로 남기면 사용 기록이 로드 소음으로 뒤덮인다. historyMiddleware 와 같은 방식으로
 * resetWorkflow ~ requestFitView 구간(또는 5초)을 로드로 보고 건너뛴다.
 */

/** 액션 타입 → 기록할 기능 이름. 여기에 없는 액션은 남기지 않는다(의미 단위 화이트리스트) */
const USAGE: Record<string, Feature> = {
  'auth/signIn/fulfilled': 'auth.login',
  'auth/signOut/fulfilled': 'auth.logout',
  'project/setSelectedProject': 'project.open',
  'project/createProject/fulfilled': 'project.create',
  'project/createWorkflow/fulfilled': 'workflow.create',
  'project/saveWorkflowCanvas/fulfilled': 'workflow.save',
  'project/saveWorkflowMeta/fulfilled': 'workflow.save',
  'project/loadWorkflowCanvas/fulfilled': 'workflow.open',
  'project/retireWorkflow/fulfilled': 'workflow.delete',
  'dataDefinitions/createItem/fulfilled': 'datadef.save',
  'dataDefinitions/copy/fulfilled': 'datadef.save',
  'workflow/addNode': 'node.add',
  'workflow/removeNode': 'node.delete',
};

/** 실패도 같은 기능 이름으로 남긴다 — 성공만 보이면 "왜 아무도 안 쓰지?" 의 답을 놓친다 */
const ERROR_OF: Record<string, Feature> = {
  'auth/signIn/rejected': 'auth.login',
  // 목록 읽기 실패는 특히 중요하다 — 화면에는 "워크플로가 없음" 과 똑같이 보여서
  // 신고가 들어오기 전까지 아무도 모른다. 기록이 없으면 사후에 확인할 방법도 없다.
  'project/loadProjects/rejected': 'project.list',
  'project/loadWorkflows/rejected': 'workflow.list',
  'project/createProject/rejected': 'project.create',
  'project/createWorkflow/rejected': 'workflow.create',
  'project/saveWorkflowCanvas/rejected': 'workflow.save',
  'project/saveWorkflowMeta/rejected': 'workflow.save',
  'project/loadWorkflowCanvas/rejected': 'workflow.open',
  'project/retireWorkflow/rejected': 'workflow.delete',
  'dataDefinitions/createItem/rejected': 'datadef.save',
};

/** 로드 주입으로 볼 수 있는 최대 시간 (historyMiddleware 와 같은 값·같은 이유) */
const LOAD_WINDOW_MS = 5000;

/** 무엇에 대한 동작이었나 — 인자를 통째로 싣지 않는다(비밀번호가 섞여 든다) */
function targetOf(type: string, action: any): string | undefined {
  if (type.startsWith('workflow/')) return action?.payload?.type || action?.payload?.id;
  if (type.startsWith('auth/')) return undefined;                 // 로그인 인자는 기록하지 않는다
  const arg = action?.meta?.arg;
  if (typeof arg === 'string') return arg;
  if (typeof action?.payload === 'string') return action.payload; // setSelectedProject 처럼 ID 하나만 오는 것
  return arg?.id || arg?.workflowId || arg?.name || action?.payload?.id;
}

export const usageLogMiddleware: Middleware = (store) => {
  let suspendedUntil = 0;

  return (next) => (action: any) => {
    const type: string = action?.type || '';

    if (type === 'workflow/resetWorkflow') suspendedUntil = Date.now() + LOAD_WINDOW_MS;
    else if (type === 'workflow/requestFitView') suspendedUntil = 0;

    const result = next(action);

    // 세션이 확정되는 자리 — 이후 기록에 사용자 이름이 붙는다(QBase 모드는 서버가 알 수 없다)
    if (type.startsWith('auth/')) {
      const u = (store.getState() as any)?.auth?.user;
      setLogUser(u?.loginId || u?.usrid || null);
    }

    try {
      const feature = USAGE[type];
      // 프로젝트 선택 해제(null)는 "열었다" 가 아니다 — 로그아웃·초기화에서도 지나간다
      if (feature === 'project.open' && !action?.payload) return result;
      if (feature) {
        // 캔버스를 여는 중의 노드 주입은 사용자의 편집이 아니다
        const isCanvasEdit = type.startsWith('workflow/');
        if (!(isCanvasEdit && Date.now() < suspendedUntil)) {
          logUsage(feature, { target: targetOf(type, action) });
        }
      }
      const errFeature = ERROR_OF[type];
      if (errFeature) {
        const err = action?.error || action?.payload || {};
        logError(errFeature, err.message ? err : new Error(String(err?.message || err || i18n.t('common:error.failed'))), {
          target: targetOf(type, action),
          where: type,
        });
      }
    } catch {
      // 기록은 절대 앱을 방해하지 않는다
    }
    return result;
  };
};
